import React, { useCallback, useContext, useState } from 'react';
import { Button, message, Modal, Space, Tooltip } from 'antd';
import { ExclamationCircleOutlined, PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';

import { useTranslation } from 'react-i18next';
import { TRY_THEN_BUY } from '../../../../constants/define/translation';
import { TTBStatus, TTBType } from '../../../../types/tryThenBuy';
import { UserContext } from '../../../../repository/context/userContext';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import {
  postPauseTTBRepository,
  postResumeTTBRepository,
} from '../../../../repository/mytm/try-then-buy/ttbRepository';
import { API_URLS } from '../../../../constants/urls/api-urls';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';
import { ModalReasonInput } from '../modals/ModalReasonInput';

interface Props {
  record: TTBType;
  onRefresh: () => void;
}
export const ContentReason = (props: any) => {
  const { reason } = props;
  return (
    <StyledContentReason>
      <div className="wrap-popover">
        <p className="nodes-value">{reason}</p>
      </div>
    </StyledContentReason>
  );
};

export const PopoverTitle = (props: any) => {
  return <span style={{ fontSize: 16, fontWeight: 'bold' }}>{props.title}</span>;
};
export default React.memo((props: Props) => {
  const { t } = useTranslation(TRY_THEN_BUY);
  const { record, onRefresh } = props;
  const userContext = useContext(UserContext);

  const [isOpenModalCancel, setIsOpenModaReason] = useState(false);
  const [loading, setLoading] = useState(false);

  const onCloseReasonModal = useCallback(() => {
    setIsOpenModaReason(false);
  }, []);
  const onOpenReasonModal = useCallback(() => {
    setIsOpenModaReason(true);
  }, []);

  const handleSubmitCancel = async (value: any) => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.cancel')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        onCloseReasonModal();
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        const updateFunc = () => {
          postPauseTTBRepository(API_URLS.PAUSE_TRY_THEN_BUY, {
            orderId: record.orderId,
            pauseReason: value.reason,
          })
            .then(() => {
              onRefresh();
            })
            .catch(e => {
              message.error(MESSAGE_ERROR.M02);
            });
        };
        if (!userContext.user?.email || userContext.user?.email === '') {
          message.error(MESSAGE_ERROR.M02);
        } else {
          setLoading(true);
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              message.error(MESSAGE_ERROR.M02);
            })
            .finally(() => {
              setLoading(false);
            });
        }
      },
    });
  };

  const handleResume = async () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={t('table.popup.cancel')}
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        const updateFunc = () => {
          postResumeTTBRepository(API_URLS.RESUME_TRY_THEN_BUY, {
            orderId: record.orderId,
          })
            .then(() => {
              onRefresh();
            })
            .catch(e => {
              message.error(MESSAGE_ERROR.M02);
            });
        };
        if (!userContext.user?.email || userContext.user?.email === '') {
          message.error(MESSAGE_ERROR.M02);
        } else {
          setLoading(true);
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              message.error(MESSAGE_ERROR.M02);
            })
            .finally(() => {
              setLoading(false);
            });
        }
      },
    });
  };
  const canPause = !record.paused;
  const canResume = record.paused;
  const exceptStatus = [
    TTBStatus.PRODUCT_RETURNING_TO_TM,
    TTBStatus.PRODUCT_RECEIVED_TO_TM,
    TTBStatus.CHARGED,
    TTBStatus.CANCELLED,
    TTBStatus.BOUGHT,
    TTBStatus.PRODUCT_RETURNED,
  ];
  const notShowPauseAction = exceptStatus.includes(record?.status as TTBStatus);
  if (notShowPauseAction) return <Space />;
  return (
    <StyledContainer>
      <Space size="small">
        {canPause && (
          <Tooltip title={t('table.tooltip.pause')}>
            <Button className="cancel" loading={loading} icon={<PauseCircleOutlined />} onClick={onOpenReasonModal} />
            <ModalReasonInput
              onSubmit={handleSubmitCancel}
              isOpenModal={isOpenModalCancel}
              onClose={onCloseReasonModal}
              title={t('form.pauseTTB')}
              label={t('form.pauseReason')}
              placeholder={t('form.placeholderEnter')}
              validateMessage=""
            />
          </Tooltip>
        )}
        {canResume && (
          <Tooltip placement="top" title={t('table.tooltip.resume')}>
            <Button className="cancel" loading={loading} icon={<PlayCircleOutlined />} onClick={handleResume} />
          </Tooltip>
        )}
      </Space>
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  // padding-left: 0.5rem;
`;
const StyledContentReason = styled.div`
  .wrap-popover {
    max-width: 600px;
  }
  p {
    margin-bottom: 0;
    font-size: 13px;
  }

  & .nodes-value {
    color: #97979d;
    font-weight: 600;
  }

  & .nodes-name {
    font-weight: 700;
  }
`;
